import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }
const _hoisted_2 = { class: "mb-6" }
const _hoisted_3 = { class: "flex items-center gap-3" }
const _hoisted_4 = { class: "p-2.5 bg-indigo-50 rounded-xl" }
const _hoisted_5 = { class: "text-2xl sm:text-3xl font-bold text-dashboard-text" }
const _hoisted_6 = {
  key: 0,
  class: "bg-white border border-dashboard-border rounded-xl p-16 text-center"
}
const _hoisted_7 = {
  key: 1,
  class: "bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
}
const _hoisted_8 = { class: "text-sm text-red-600 mt-1" }
const _hoisted_9 = {
  key: 2,
  class: "bg-white border border-dashboard-border rounded-xl p-12 text-center"
}
const _hoisted_10 = {
  key: 3,
  class: "grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
}
const _hoisted_11 = {
  key: 4,
  class: "space-y-3"
}
const _hoisted_12 = { class: "border-b border-dashboard-border bg-dashboard-bg px-4 py-2 flex items-center gap-2 text-xs" }
const _hoisted_13 = {
  key: 0,
  class: "font-mono text-dashboard-text"
}
const _hoisted_14 = {
  key: 1,
  class: "text-dashboard-muted italic"
}
const _hoisted_15 = { class: "text-dashboard-muted" }
const _hoisted_16 = { class: "divide-y divide-dashboard-border" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "flex-1 min-w-0" }
const _hoisted_19 = { class: "flex items-center gap-2 flex-wrap" }
const _hoisted_20 = { class: "font-medium text-dashboard-text" }
const _hoisted_21 = { class: "text-xs text-dashboard-muted mt-0.5 flex items-center gap-2 flex-wrap" }
const _hoisted_22 = { key: 2 }
const _hoisted_23 = {
  key: 3,
  class: "font-mono"
}

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  ArrowLeft, Mail, History, AlertCircle, Activity, Users, ShieldAlert,
  Link as LinkIcon, ChevronRight,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuditLogDetailPanel from '@/components/audit/AuditLogDetailPanel.vue'
import StatCard from '@/components/audit/StatCard.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import {
  SEVERITY_META, getActionLabel, getDomainMeta, formatDateTime,
} from '@/utils/auditMeta'
import type { AuditLogEntry } from '@/utils/auditMeta'


export default /*@__PURE__*/_defineComponent({
  __name: 'CourrierTimelineView',
  setup(__props) {

const route = useRoute()
const toast = useToast()

const courrierId = computed(() => Number(route.params.id))
const timeline = ref<AuditLogEntry[]>([])
const loading = ref(false)
const error = ref('')
const selectedLog = ref<AuditLogEntry | null>(null)

const uniqueUsers = computed(() =>
  new Set(timeline.value.filter(e => e.user).map(e => e.user!.id)).size,
)
const criticalCount = computed(() =>
  timeline.value.filter(e => e.severity === 'critical').length,
)
const uniqueRequestIds = computed(() =>
  new Set(timeline.value.filter(e => e.request_id).map(e => e.request_id!)).size,
)

const groupedEntries = computed(() => {
  const groups: Array<{ key: string; requestId: string | null; entries: AuditLogEntry[] }> = []
  const seen = new Map<string, number>()
  for (const entry of timeline.value) {
    const key = entry.request_id || `_no_req_${entry.id}`
    if (seen.has(key)) {
      groups[seen.get(key)!].entries.push(entry)
    } else {
      seen.set(key, groups.length)
      groups.push({ key, requestId: entry.request_id || null, entries: [entry] })
    }
  }
  return groups
})

async function fetchTimeline() {
  loading.value = true
  error.value = ''
  try {
    const result = await apiFetch(`/api/admin/audit/courrier/${courrierId.value}/`)
    timeline.value = result.timeline || []
  } catch (e: any) {
    error.value = e?.message || 'Erreur'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

watch(courrierId, fetchTimeline)
onMounted(fetchTimeline)

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_router_link, {
            to: "/audit",
            class: "text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(ArrowLeft), { class: "w-4 h-4" }),
              _cache[2] || (_cache[2] = _createTextVNode(" Retour aux logs d'audit "))
            ]),
            _: 1,
            __: [2]
          }),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(Mail), { class: "w-6 h-6 text-indigo-600" })
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("h1", _hoisted_5, "Timeline — Courrier #" + _toDisplayString(courrierId.value), 1),
              _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-sm text-dashboard-muted mt-0.5" }, " Toutes les actions enregistrées sur ce courrier, classées chronologiquement. ", -1))
            ])
          ])
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[4] || (_cache[4] = [
              _createElementVNode("div", { class: "w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-4" }, null, -1),
              _createElementVNode("p", { class: "text-dashboard-muted" }, "Chargement…", -1)
            ])))
          : (error.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(_unref(AlertCircle), { class: "w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" }),
                _createElementVNode("div", null, [
                  _cache[5] || (_cache[5] = _createElementVNode("p", { class: "font-medium text-red-800" }, "Erreur", -1)),
                  _createElementVNode("p", _hoisted_8, _toDisplayString(error.value), 1)
                ])
              ]))
            : (!timeline.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createVNode(_unref(History), { class: "w-12 h-12 text-dashboard-muted mx-auto mb-3" }),
                  _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-dashboard-muted" }, "Aucune action enregistrée pour ce courrier.", -1))
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _createVNode(StatCard, {
                    label: "Actions totales",
                    value: String(timeline.value.length),
                    icon: _unref(Activity),
                    tone: "slate"
                  }, null, 8, ["value", "icon"]),
                  _createVNode(StatCard, {
                    label: "Utilisateurs distincts",
                    value: String(uniqueUsers.value),
                    icon: _unref(Users),
                    tone: "indigo"
                  }, null, 8, ["value", "icon"]),
                  _createVNode(StatCard, {
                    label: "Actions critiques",
                    value: String(criticalCount.value),
                    icon: _unref(ShieldAlert),
                    tone: "red"
                  }, null, 8, ["value", "icon"]),
                  _createVNode(StatCard, {
                    label: "Sessions corrélées",
                    value: String(uniqueRequestIds.value),
                    icon: _unref(LinkIcon),
                    tone: "blue"
                  }, null, 8, ["value", "icon"])
                ])),
        (timeline.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedEntries.value, (group) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: group.key,
                  class: "bg-white border border-dashboard-border rounded-xl overflow-hidden"
                }, [
                  _createElementVNode("div", _hoisted_12, [
                    _createVNode(_unref(LinkIcon), { class: "w-3.5 h-3.5 text-dashboard-muted" }),
                    _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-dashboard-muted" }, "Session / requête", -1)),
                    (group.requestId)
                      ? (_openBlock(), _createElementBlock("code", _hoisted_13, _toDisplayString(group.requestId), 1))
                      : (_openBlock(), _createElementBlock("span", _hoisted_14, "— sans request_id —")),
                    _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-dashboard-muted ml-2" }, "·", -1)),
                    _createElementVNode("span", _hoisted_15, _toDisplayString(group.entries.length) + " action(s)", 1),
                    (group.requestId)
                      ? (_openBlock(), _createBlock(_component_router_link, {
                          key: 2,
                          to: `/audit/request/${group.requestId}`,
                          class: "text-blue-600 hover:underline ml-auto"
                        }, {
                          default: _withCtx(() => _cache[7] || (_cache[7] = [
                            _createTextVNode("Vue corrélation ›")
                          ])),
                          _: 2,
                          __: [7]
                        }, 1032, ["to"]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_16, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.entries, (entry) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: entry.id,
                        onClick: ($event: any) => (selectedLog.value = entry),
                        class: _normalizeClass([
                'w-full text-left px-4 py-3 hover:bg-dashboard-bg transition-colors flex items-start gap-3 border-l-4',
                _unref(SEVERITY_META)[entry.severity].ring,
              ])
                      }, [
                        (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(getDomainMeta)(entry.action).icon), { class: "w-4 h-4 mt-0.5 text-dashboard-muted flex-shrink-0" })),
                        _createElementVNode("div", _hoisted_18, [
                          _createElementVNode("div", _hoisted_19, [
                            _createElementVNode("span", _hoisted_20, _toDisplayString(_unref(getActionLabel)(entry.action)), 1),
                            (entry.severity !== 'info')
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 0,
                                  class: _normalizeClass(['text-[10px] px-1.5 py-0.5 rounded font-medium', _unref(SEVERITY_META)[entry.severity].badge])
                                }, _toDisplayString(_unref(SEVERITY_META)[entry.severity].label), 3))
                              : _createCommentVNode("", true)
                          ]),
                          _createElementVNode("div", _hoisted_21, [
                            (entry.user)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _cache[10] || (_cache[10] = _createTextVNode(" par ")),
                                  _createVNode(_component_router_link, {
                                    to: `/audit/user/${entry.user.id}`,
                                    onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                                    class: "text-blue-600 hover:underline"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(entry.user.full_name), 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["to"])
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _cache[11] || (_cache[11] = _createTextVNode("par ")),
                                  _cache[12] || (_cache[12] = _createElementVNode("span", { class: "italic" }, "système", -1))
                                ], 64)),
                            _cache[13] || (_cache[13] = _createElementVNode("span", null, "·", -1)),
                            _createElementVNode("span", null, _toDisplayString(_unref(formatDateTime)(entry.created_at)), 1),
                            (entry.ip_address)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_22, "·"))
                              : _createCommentVNode("", true),
                            (entry.ip_address)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(entry.ip_address), 1))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _createVNode(_unref(ChevronRight), { class: "w-4 h-4 text-dashboard-muted flex-shrink-0 mt-1" })
                      ], 10, _hoisted_17))
                    }), 128))
                  ])
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ]),
      (selectedLog.value)
        ? (_openBlock(), _createBlock(AuditLogDetailPanel, {
            key: 0,
            log: selectedLog.value,
            onClose: _cache[1] || (_cache[1] = ($event: any) => (selectedLog.value = null))
          }, null, 8, ["log"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})